import clsx from 'clsx';
import { forwardRef } from 'react';

import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useSafeForwardProps } from '../../core/hooks/useSafeForwardProps.js';
import type { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { DOMProps } from '../../core/types/dom.js';
import type { _HeadingTag } from '../../core/types/heading.js';
import type { MaskingProps } from '../../core/types/masking-props.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import type { WithChildren } from '../../core/types/with-children.js';
import { headingCSS } from './Heading.sty.js';

const COMPONENT_NAME = 'Heading';

/**
 * Props used for the Heading component
 * @public
 */
export interface HeadingProps
  extends
    DOMProps,
    WithChildren,
    AriaLabelingProps,
    StylingProps,
    DataTestId,
    MaskingProps,
    BehaviorTrackingProps {
  /**
   * The visual level of the heading.
   * @defaultValue 1
   */
  level?: 1 | 2 | 3 | 4 | 5 | 6;
  /**
   * The polymorphed HTML tag that determines the semantic level of the heading.
   * @defaultValue 'h1'
   */
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

/**
 * Use the `Heading` to render semantic HTML heading elements (`h1`, `h2`, etc.).
 * The component allows you to independently define both the visual and the semantic level of the heading.
 * @public
 */
export const Heading = /* @__PURE__ */ forwardRef<
  HTMLHeadingElement,
  HeadingProps
>((props, forwardedRef) => {
  const {
    level,
    children,
    as,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    ...remainingProps
  } = props;

  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(remainingProps);
  const ariaLabelingProps = useAriaLabelingProps(restProps);
  const forwardProps = useSafeForwardProps(restProps, ariaLabelingProps);

  const visualLevel = level ?? 1;
  // semantic level
  const HeadingTag: _HeadingTag = as ?? (`h${visualLevel}` as _HeadingTag);

  return (
    <HeadingTag
      ref={forwardedRef}
      className={clsx(headingCSS({ visualLevel }), consumerClassName)}
      style={consumerStyle}
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      {...ariaLabelingProps}
      {...behavioralTrackingProps}
      {...forwardProps}
      data-dt-component={COMPONENT_NAME}
    >
      {children}
    </HeadingTag>
  );
});

(Heading as typeof Heading & { displayName: string }).displayName =
  COMPONENT_NAME;
